import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { createImpactNetwork } from "../../services/impactNetworks";
import { CFR_CATEGORIES } from "../../constants/cfrCategories";
import "./ImpactNetworks.css";

export default function CreateImpactNetwork() {
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [focusAreas, setFocusAreas] = useState([]);
  const [visibility, setVisibility] = useState("private");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggleFocusArea = (area) => {
    setFocusAreas((prev) =>
      prev.includes(area)
        ? prev.filter((x) => x !== area)
        : [...prev, area]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await createImpactNetwork({
        user,
        name,
        description,
        focusAreas,
        visibility,
      });

      navigate(`/donor/impact-networks/${result.networkId}`);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to create Impact Network.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="impact-network-page">
        Please log in to create an Impact Network.
      </div>
    );
  }

  return (
    <div className="impact-network-page">
      <nav className="network-top-nav">
        <Link to="/" className="network-brand-link">
          somebody.gives
        </Link>

        <div className="network-top-nav-links">
          <Link to="/donor/impact-networks">My Networks</Link>
          <Link to="/give/dashboard">My Dashboard</Link>
          <Link to="/give/browse">Browse Needs</Link>
        </div>
      </nav>

      <section className="network-hero">
        <div>
          <div className="network-eyebrow">Create a Group</div>
          <h1>Create Impact Network</h1>
          <p className="network-hero-text">
            Build a shared giving identity for a club, workplace, church,
            family, or civic group.
          </p>
          <p className="network-hero-subtext">
            Members still fund needs personally. The network tracks collective
            impact.
          </p>
        </div>

        <Link className="network-secondary-button" to="/donor/impact-networks">
          My Networks
        </Link>
      </section>

      {error && <div className="network-error-box">{error}</div>}

      <section className="network-panel">
        <div className="network-eyebrow">Network Details</div>
        <h2>Basic Information</h2>

        <form onSubmit={handleSubmit} className="network-form">
          <label>
            Network Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Springfield Rotary"
              required
            />
          </label>

          <label>
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A group working together to support recurring local nonprofit needs."
            />
          </label>

          <div>
            <div className="network-form-label">Visibility</div>
            <p className="network-form-help">
              Private networks are only visible to members. Public networks may
              be shown on future SomebodyGives public impact pages.
            </p>

            <div className="network-radio-group">
              <label>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === "private"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <span>
                  <strong>Private</strong> — Only members can view this network.
                </span>
              </label>

              <label>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === "public"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <span>
                  <strong>Public</strong> — May appear on public impact pages.
                </span>
              </label>
            </div>
          </div>

          <div>
            <div className="network-form-label">Preferred Focus Areas</div>
            <p className="network-form-help">
              These help describe the group’s interests. Members can still
              support any available need.
            </p>

            <div className="network-checkbox-grid">
              {CFR_CATEGORIES.map((area) => (
  <label key={area.value}>
    <input
      type="checkbox"
      checked={focusAreas.includes(area.value)}
      onChange={() => toggleFocusArea(area.value)}
    />
    <span>
      {area.icon} {area.label}
    </span>
  </label>
))}
            </div>
          </div>

          <button className="network-primary-button" disabled={loading}>
            {loading ? "Creating..." : "Create Network"}
          </button>
        </form>
      </section>
    </div>
  );
}